import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, ShieldCheck, SkipForward, Clock } from 'lucide-react';
import { formatINR } from './payoutUtils';
import { BulkUtrTab, BulkScreenshotTab, BulkExcelTab } from './bulkSettlementTabs';

const METHODS = [
  { key: 'excel', label: 'Excel Upload', desc: 'Upload a settlement sheet — rows are auto-matched to the selected payouts' },
  { key: 'utr', label: 'UTR Entry', desc: 'Enter the bank UTR for each selected payout' },
  { key: 'screenshot', label: 'Screenshot Upload', desc: 'Upload a payment screenshot / PDF proof for each payout' },
];
const COMPLETABLE = ['approved', 'processing'];

// Bulk Payout Settlement: NO payout can be marked Completed without validated proof (Excel / UTR / Screenshot).
export const BulkSettlementModal = ({ payouts, open, onOpenChange, onDone }) => {
  const { api } = useAuth();
  const [method, setMethod] = useState('excel');
  const [busy, setBusy] = useState(false);
  const [utrs, setUtrs] = useState({});
  const [validation, setValidation] = useState(null);
  const [excelBatch, setExcelBatch] = useState(null);
  const [result, setResult] = useState(null);

  const { eligible, skipped } = useMemo(() => ({
    eligible: payouts.filter(p => COMPLETABLE.includes(p.status)),
    skipped: payouts.filter(p => !COMPLETABLE.includes(p.status)),
  }), [payouts]);
  const total = eligible.reduce((s, p) => s + (p.amount || 0), 0);

  useEffect(() => {
    if (!open) return;
    setMethod('excel'); setUtrs({}); setValidation(null); setExcelBatch(null); setResult(null);
  }, [open]);

  const items = () => eligible.map(p => ({ payout_id: p.id, utrs: method === 'utr' ? [{ utr: (utrs[p.id] || '').trim(), amount: p.amount }] : [] }));

  const validate = async () => {
    setBusy(true);
    try {
      const r = await api.post('/payout-settlements/bulk/validate', { method, items: items() });
      setValidation(r.data);
      r.data.valid_count === 0 ? toast.error('No payout passed proof validation') : toast.success(`${r.data.valid_count} of ${r.data.total} payouts validated`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Validation failed'); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      let r;
      if (method === 'excel') {
        r = await api.post(`/payout-settlements/excel/${excelBatch.batch_id}/confirm`, { payout_ids: eligible.map(p => p.id) });
      } else {
        r = await api.post('/payout-settlements/bulk/complete', { method, items: items() },
          { headers: { 'Idempotency-Key': `bulk-settle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` } });
      }
      setResult(r.data);
      toast.success(`${r.data.completed} payout(s) completed`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Completion failed'); }
    finally { setBusy(false); }
  };

  const validCount = method === 'excel' ? (excelBatch?.summary?.completable_payouts || 0) : (validation?.valid_count || 0);
  const canComplete = validCount > 0;
  const changeMethod = (k) => { setMethod(k); setValidation(null); setExcelBatch(null); };

  const unmatched = result?.unmatched_payouts || [];
  const failed = (result?.results || []).filter(r => r.status === 'failed');
  const completed = (result?.results || []).filter(r => r.status === 'completed');

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-4xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="bulk-settlement-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" />{result ? 'Bulk Settlement Result' : 'Bulk Payout Settlement'}</DialogTitle>
          <DialogDescription>{result ? `${result.completed} completed · ${result.failed} failed · ${unmatched.length + skipped.length} left pending` : 'Payment proof is mandatory. Payouts are marked Completed only after their proof is validated — unmatched payouts stay pending.'}</DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="bulk-settle-summary">
              <div className="p-2.5 rounded-sm border border-border/60"><p className="text-[10px] uppercase text-muted-foreground">Selected</p><p className="font-semibold" data-testid="bulk-settle-selected">{payouts.length}</p></div>
              <div className="p-2.5 rounded-sm border border-green-500/30 bg-green-500/5"><p className="text-[10px] uppercase text-muted-foreground">Eligible</p><p className="font-semibold text-green-600" data-testid="bulk-settle-eligible">{eligible.length}</p></div>
              <div className="p-2.5 rounded-sm border border-border/60"><p className="text-[10px] uppercase text-muted-foreground">Skipped</p><p className="font-semibold" data-testid="bulk-settle-skipped">{skipped.length}</p></div>
              <div className="p-2.5 rounded-sm border border-border/60"><p className="text-[10px] uppercase text-muted-foreground">Eligible Amount</p><p className="font-semibold font-mono" data-testid="bulk-settle-amount">{formatINR(total)}</p></div>
            </div>
            {skipped.length > 0 && <p className="text-xs text-muted-foreground flex items-start gap-1.5"><SkipForward className="h-3.5 w-3.5 mt-0.5 shrink-0" />Not eligible (must be Approved or Processing): {skipped.map(p => p.order_id).join(', ')}</p>}

            <div className="grid sm:grid-cols-3 gap-2" role="radiogroup">
              {METHODS.map(m => (
                <button key={m.key} type="button" role="radio" aria-checked={method === m.key} onClick={() => changeMethod(m.key)} disabled={busy}
                  className={`text-left p-3 rounded-sm border transition-colors ${method === m.key ? 'border-primary bg-primary/5' : 'border-border/60 hover:bg-accent/40'}`} data-testid={`bulk-method-${m.key}`}>
                  <p className="text-sm font-medium flex items-center gap-2"><span className={`w-3 h-3 rounded-full border ${method === m.key ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />{m.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{m.desc}</p>
                </button>
              ))}
            </div>

            {eligible.length === 0 ? (
              <div className="p-6 border border-dashed border-border/60 rounded-sm text-center text-sm text-muted-foreground" data-testid="bulk-settle-none">None of the selected payouts can be completed. Only Approved or Processing payouts accept settlement proof.</div>
            ) : (
              <>
                {method === 'utr' && <BulkUtrTab payouts={eligible} utrs={utrs} setUtrs={(u) => { setUtrs(u); setValidation(null); }} validation={validation} />}
                {method === 'screenshot' && <BulkScreenshotTab api={api} payouts={eligible} validation={validation} onChanged={() => setValidation(null)} />}
                {method === 'excel' && <BulkExcelTab api={api} payouts={eligible} batch={excelBatch} setBatch={setExcelBatch} busy={busy} setBusy={setBusy} />}
              </>
            )}

            {validation && method !== 'excel' && (
              <div className={`p-3 rounded-sm border text-sm flex items-center justify-between ${validation.valid_count > 0 ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/40 bg-red-500/5'}`} data-testid="bulk-validation-summary">
                <span>{validation.valid_count} valid · {validation.invalid_count} failed validation</span>
                <span className="font-mono font-semibold">{formatINR(validation.valid_amount)}</span>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={busy} data-testid="bulk-settle-cancel-btn">Cancel</Button>
              {method !== 'excel' && !validation && (
                <Button className="rounded-sm" disabled={busy || eligible.length === 0} onClick={validate} data-testid="bulk-settle-validate-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Validate Proof</Button>
              )}
              {(method === 'excel' ? !!excelBatch : !!validation) && (
                <Button className="rounded-sm bg-green-600 hover:bg-green-700 text-white" disabled={busy || !canComplete} onClick={complete} data-testid="bulk-settle-complete-btn">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Complete {validCount} Validated Payout{validCount === 1 ? '' : 's'}
                </Button>
              )}
            </DialogFooter>
          </>
        )}

        {result && (
          <div className="space-y-3" data-testid="bulk-settle-result">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="p-2.5 rounded-sm border border-green-500/30 bg-green-500/5"><p className="text-[10px] uppercase text-muted-foreground">Completed</p><p className="font-semibold text-green-600" data-testid="bulk-result-completed">{result.completed} · {formatINR(result.total_amount)}</p></div>
              <div className="p-2.5 rounded-sm border border-red-500/30 bg-red-500/5"><p className="text-[10px] uppercase text-muted-foreground">Failed</p><p className="font-semibold text-red-600" data-testid="bulk-result-failed">{result.failed}</p></div>
              <div className="p-2.5 rounded-sm border border-amber-500/30 bg-amber-500/5"><p className="text-[10px] uppercase text-muted-foreground">Still Pending</p><p className="font-semibold text-amber-600" data-testid="bulk-result-pending">{unmatched.length + skipped.length}</p></div>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-border/40 text-sm rounded-sm border border-border/50">
              {completed.map(r => <div key={r.payout_id} className="flex items-center justify-between gap-3 px-3 py-1.5" data-testid={`bulk-result-row-${r.order_id}`}><span className="font-mono text-xs">{r.order_id}</span><span className="text-green-600 text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Completed · {formatINR(r.amount)} · {(r.settlement_ids || []).length} receipt(s)</span></div>)}
              {failed.map(r => <div key={r.payout_id} className="flex items-center justify-between gap-3 px-3 py-1.5" data-testid={`bulk-result-row-${r.order_id}`}><span className="font-mono text-xs">{r.order_id}</span><span className="text-red-600 text-xs inline-flex items-center gap-1 text-right"><XCircle className="h-3.5 w-3.5 shrink-0" />{r.reason}</span></div>)}
              {unmatched.map(r => <div key={r.payout_id} className="flex items-center justify-between gap-3 px-3 py-1.5" data-testid={`bulk-result-row-${r.order_id}`}><span className="font-mono text-xs">{r.order_id}</span><span className="text-amber-600 text-xs inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{r.reason} · stays pending</span></div>)}
              {skipped.map(p => <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-1.5"><span className="font-mono text-xs">{p.order_id}</span><span className="text-muted-foreground text-xs">Skipped · not eligible ({p.status})</span></div>)}
            </div>
            <DialogFooter><Button className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="bulk-settle-close-btn">Close</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
